import { type FC, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router'
import { ErrorBox } from '@/components/commons/error-box'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertCircleIcon, LayoutRightIcon } from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import { MultipleFlagsPopoverTrigger } from '@/features/photoset/components/flags-popover/multiple-flags-popover-trigger'
import { GalleryInitialQaModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-initial-qa-modal/gallery-initial-qa-modal'
import { ImageCardFlags } from '@/features/photoset/components/image-card-flags'
import { ImagesCounter } from '@/features/photoset/components/images-counter'
import { ImagesList } from '@/features/photoset/components/images-list'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import { type PhotosetURLParams, StepKey } from '@/features/photoset/types.ts'
import { handleImagesRangeSelect } from '@/features/photoset/utils.ts'
import { useTranslation } from '@/translations/i18n'

const InitialQaEndSlot = ({
	selectedImagesCount,
	selectedImages,
	hasImages,
	unselectAll,
	setLastSelectedImageId,
	selectAll
}: {
	selectedImagesCount: number
	selectedImages: number[]
	hasImages: boolean
	unselectAll: () => void
	setLastSelectedImageId: (id: number | null) => void
	selectAll: () => void
}) => {
	const { t } = useTranslation()
	if (selectedImagesCount >= 1) {
		return (
			<div className='flex items-center gap-2'>
				<ImagesCounter count={selectedImagesCount} />

				<FlagsPopover
					selectedImagesIds={selectedImages}
					stepId={StepKey['initial-qa']}
					trigger={<MultipleFlagsPopoverTrigger />}
				/>

				<ButtonLegacy
					onClick={() => {
						unselectAll()
						setLastSelectedImageId(null)
					}}
					size='sm'
					type='button'
					variant='tertiary'
				>
					{t('common:unselect_all')}
				</ButtonLegacy>
			</div>
		)
	}
	if (hasImages) {
		return (
			<ButtonLegacy
				onClick={() => {
					selectAll()
					setLastSelectedImageId(null)
				}}
				size='sm'
				type='button'
				variant='tertiary'
			>
				{t('common:select_all')}
			</ButtonLegacy>
		)
	}
	return null
}

const InitialQaTitle = ({
	isLoading,
	selectedImagesCount,
	remappedImagesCount
}: {
	isLoading: boolean
	selectedImagesCount: number
	remappedImagesCount: number
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return <Skeleton height={30} width={96} />
	}
	if (selectedImagesCount >= 1) {
		return t('common:x_selected_images', {
			selected: selectedImagesCount,
			count: remappedImagesCount
		})
	}
	return t('common:x_images', {
		count: remappedImagesCount
	})
}

interface InitialQaImagesPoolProps {
	hasSidebar: boolean
	showSidebar: () => void
}

export const InitialQaImagesPool: FC<InitialQaImagesPoolProps> = ({
	hasSidebar,
	showSidebar
}) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const images = usePhotosetStore((state) => state.images)
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const selectAll = usePhotosetStore((state) => state.selectAll)
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const unselectImages = usePhotosetStore((state) => state.unselectImages)
	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const [lastSelectedImageId, setLastSelectedImageId] = useState<
		number | null
	>(null)

	const photosetQuery = useGetPhotoset(
		{
			id: urlParams.id
		},
		{ enabled: false }
	)

	const imagesQuery = useGetPhotosetImagesList(
		{
			urlParams: { photosetId: urlParams.id }
		},
		{ enabled: false }
	)

	const remappedImages = [...images.values()]

	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		onExpandImage
	} = useExpandPhotosetImage()

	const bedroomsCount = useMemo(
		() => photosetQuery.data?.assignment.task.entity.bedrooms,
		[photosetQuery]
	)

	return (
		<div className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'>
			<ListHeader
				endSlot={
					<div className='flex items-center'>
						<InitialQaEndSlot
							hasImages={!!imagesQuery.data?.length}
							selectAll={selectAll}
							selectedImages={selectedImages}
							selectedImagesCount={selectedImages.length}
							setLastSelectedImageId={setLastSelectedImageId}
							unselectAll={unselectAll}
						/>

						{!hasSidebar && (
							<ButtonLegacy
								onClick={showSidebar}
								shape='square'
								type='button'
								variant='tertiary'
							>
								<LayoutRightIcon />
							</ButtonLegacy>
						)}
					</div>
				}
				size='sm'
				startSlot={
					!imagesQuery.isLoading && (
						<p>
							{t('common:x_bedrooms', {
								count: bedroomsCount ?? 0
							})}
						</p>
					)
				}
				title={
					<InitialQaTitle
						isLoading={imagesQuery.isLoading}
						remappedImagesCount={remappedImages.length}
						selectedImagesCount={selectedImages.length}
					/>
				}
			/>
			{imagesQuery.isError ? (
				<ErrorBox
					icon={<AlertCircleIcon />}
					title={t('errors:default')}
				/>
			) : (
				<ScrollArea scrollbar={<Scrollbar />}>
					<ImagesList>
						{imagesQuery.isPending
							? Array.from(
									{ length: 20 },
									(_, i) => `skeleton-${i}`
								).map((key) => (
									<ImageCard asSkeleton key={key} />
								))
							: photosetQuery.data &&
								remappedImages.map((image, index, array) => {
									const isSelected = selectedImages.some(
										(selectedImageId) =>
											selectedImageId === image.id
									)
									return (
										<ImageCard
											endSlot={
												<>
													<ExpandImageButton
														onClick={() =>
															onExpandImage(
																null,
																index
															)
														}
													/>
													<ImageCardFlags
														image={image}
														stepId={
															StepKey[
																'initial-qa'
															]
														}
													/>
												</>
											}
											image={image}
											isSelected={isSelected}
											key={image.id}
											onSelect={(isShiftPressed) => {
												handleImagesRangeSelect({
													array,
													image,
													index,
													isSelected,
													isShiftPressed,
													lastSelectedImageId,
													selectImages,
													setLastSelectedImageId,
													unselectImages
												})
											}}
											thumbnail={image.thumbnails?.xs}
										/>
									)
								})}
					</ImagesList>
				</ScrollArea>
			)}

			{currentDetailImageIndex !== null &&
				currentDetailImageIndex >= 0 && (
					<GalleryInitialQaModal
						currentDetailImageIndex={currentDetailImageIndex}
						images={remappedImages}
						onClose={() => setCurrentDetailImageIndex(null)}
						setCurrentDetailImageIndex={setCurrentDetailImageIndex}
					/>
				)}
		</div>
	)
}

InitialQaImagesPool.displayName = 'InitialQaImagesPool'
